import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { resetALL } from "../../../../../../../../../.globals/redux/actions";
import { endpoints } from "../../../../../../../../../core/defaultValues";
import {
    clientMutation,
    clientQuery,
} from "../../../../../../../../../core/data/GraphQLApi";
import { ExceptionManager } from "../../../../../../../../../core/logManager";

const INIT_STATE = {
    loading: false,
    error: null,
    create: null,
    update: null,
    workOrder: null,
    loadingWorkOrder: null,
    workOrderTypes: [],
    loadingWorkOrderTypes: false
};

const route = "modules/workOrders/views/trackerTab/modals/createEditWorkOrder/views/settings/redux/slice";

export const workOrdersSettingsCreateWorkOrder = createAsyncThunk(
    "workOrders/workOrdersSettingsCreateWorkOrder/createWorkOrder",
    async (payload, { rejectWithValue }) => {

        try {
            const mutation = `
              mutation (
                    $customerId: Int!
                    $customerLocationId: Int!
                    $workOrderTypeId: Int!
                    $description: String
                    $driverId: Int!
                    $scheduledDate: String!
                    $closedDate: String
                    $estimatedDurationMins: Int!
                    $severity: String!
                    $customerContactId: Int
                    $dynamicFormId: Int
              ) {
                  data: createWorkOrder (
                        customerId: $customerId
                        customerLocationId: $customerLocationId
                        workOrderTypeId: $workOrderTypeId
                        description: $description
                        driverId: $driverId
                        scheduledDate: $scheduledDate
                        closedDate: $closedDate
                        estimatedDurationMins: $estimatedDurationMins
                        severity: $severity
                        customerContactId: $customerContactId
                        dynamicFormId: $dynamicFormId
                  ) {
                    id
                  }
              }
          `;

            const response = await clientMutation(
                mutation,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data;
        } catch (exc) {
            ExceptionManager(exc, route, "createWorkOrder");
            return rejectWithValue(exc);
        }
    }
);

export const workOrdersSettingsSearchWorkOrderTypes = createAsyncThunk(
    "workOrders/workOrdersSettingsSearchWorkOrderTypes/searchWorkOrderTypes",
    async ({ }, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                    query ($conditions: [WorkOrderTypeSearchInput], $sort: [WorkOrderTypeSortInput], $limit: Int, $offset: Int) {
                        data: searchWorkOrderTypes(
                            conditions: $conditions
                            sort: $sort
                            limit: $limit
                            offset: $offset
                        ) {
                            total
                            items {
                                id
                                name
                                description
                                dynamicFormId
                                dynamicForm {
                                    id
                                    name
                                }
                                estimatedDurationMins
                            }
                        }
                    }
                `,
                {
                    conditions: [],
                    sort: {
                        "field": "name",
                        "order": "ASC"
                    }
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data?.items || []

        } catch (exc) {
            ExceptionManager(exc, route, "searchWorkOrderTypes");
            return rejectWithValue(exc);
        }
    }
);

export const workOrdersSettingsGetWorkOrder = createAsyncThunk(
    "workOrders/workOrdersSettingsSearchWorkOrderTypes/getWorkOrder",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                    query ($id: Int!) {
                        data: getWorkOrder(
                            id: $id
                        ) {
                            id
                            customerId
                            customer {
                                id
                                name
                                tags {
                                    id
                                    label
                                    color
                                }
                                tagIds
                            }
                            customerLocationId
                            customerLocation {
                                id
                                name
                                address
                            }
                            customerContactId
                            customerContact {
                                id
                                name
                                email
                                phone
                            }
                            workOrderTypeId
                            workOrderType {
                                id
                                name
                                dynamicFormId
                                dynamicForm {
                                    id
                                    name
                                }
                            }
                            workOrderNumber
                            description
                            driverId
                            driver {
                                id
                                title
                                unit {
                                    id
                                    label 
                                }
                                avatarFile {
                                    id
                                    repositoryPath
                                    fileKey
                                }   
                            }
                            scheduledDate
                            closedDate
                            estimatedDurationMins
                            severity
                            statusId
                            status {
                                id
                                name
                                color
                            }
                            statusUpdatedOn
                            dynamicFormAnswerId
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data

        } catch (exc) {
            ExceptionManager(exc, route, "getWorkOrder");
            return rejectWithValue(exc);
        }
    }
);

export const workOrdersSettingsEditWorkOrder = createAsyncThunk(
    "workOrders/workOrdersSettingsCreateWorkOrder/updateWorkOrder",
    async (payload, { rejectWithValue }) => {

        try {
            const mutation = `
              mutation (
                    $id: Int!
                    $customerId: Int
                    $customerLocationId: Int
                    $workOrderTypeId: Int
                    $description: String
                    $driverId: Int
                    $scheduledDate: String
                    $closedDate: String
                    $estimatedDurationMins: Int
                    $severity: String
                    $customerContactId: Int
                    $statusId: Int
                    $dynamicFormId: Int
              ) {
                  data: updateWorkOrder (
                        id: $id
                        customerId: $customerId
                        customerLocationId: $customerLocationId
                        workOrderTypeId: $workOrderTypeId
                        description: $description
                        driverId: $driverId
                        scheduledDate: $scheduledDate
                        closedDate: $closedDate
                        estimatedDurationMins: $estimatedDurationMins
                        severity: $severity
                        customerContactId: $customerContactId
                        statusId: $statusId
                        dynamicFormId: $dynamicFormId
                  ) {
                    id
                  }
              }
          `;

            const response = await clientMutation(
                mutation,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data;
        } catch (exc) {
            ExceptionManager(exc, route, "createWorkOrder");
            return rejectWithValue(exc);
        }
    }
);

const workOrdersSettingsWorkOrderRedux = createSlice({
    name: "workOrders/settings/createEditWorkOrder",
    initialState: INIT_STATE,
    reducers: {
        resetWorkOrderSettings(state, action) {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //workOrdersSettingsCreateWorkOrder
        builder.addCase(workOrdersSettingsCreateWorkOrder.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(workOrdersSettingsCreateWorkOrder.fulfilled, (state, action) => {
            state.loading = false;
            state.create = action.payload;
        });
        builder.addCase(workOrdersSettingsCreateWorkOrder.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //workOrdersSettingsSearchWorkOrderTypes
        builder.addCase(workOrdersSettingsSearchWorkOrderTypes.pending, (state) => {
            state.loadingWorkOrderTypes = true;
        });
        builder.addCase(workOrdersSettingsSearchWorkOrderTypes.fulfilled, (state, action) => {
            state.loadingWorkOrderTypes = false;
            state.workOrderTypes = action.payload;
        });
        builder.addCase(workOrdersSettingsSearchWorkOrderTypes.rejected, (state, action) => {
            state.loadingWorkOrderTypes = false;
            state.error = action.payload;
        });

        //workOrdersSettingsGetWorkOrder
        builder.addCase(workOrdersSettingsGetWorkOrder.pending, (state) => {
            state.loadingWorkOrder = true;
        });
        builder.addCase(workOrdersSettingsGetWorkOrder.fulfilled, (state, action) => {
            state.loadingWorkOrder = false;
            state.workOrder = action.payload;
        });
        builder.addCase(workOrdersSettingsGetWorkOrder.rejected, (state, action) => {
            state.loadingWorkOrder = false;
            state.error = action.payload;
        });

        //workOrdersSettingsEditWorkOrder
        builder.addCase(workOrdersSettingsEditWorkOrder.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(workOrdersSettingsEditWorkOrder.fulfilled, (state, action) => {
            state.loading = false;
            state.update = action.payload;
        });
        builder.addCase(workOrdersSettingsEditWorkOrder.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = workOrdersSettingsWorkOrderRedux;

// Extract and export each action creator by name
export const {
    resetWorkOrderSettings,
    /*  openModalCreateEditWorkOrder, */
} = actions;

// Export the reducer, either as a default or named export
export default reducer;
